import { useEffect, useState } from "react";

import { ConfirmationDialog, Row } from "@/components";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { useCanLinkJobs } from "@/hooks/useCanLinkJobs";

// Duplicating is the primary way linked jobs get created, so the dialog
// SUGGESTS linking the copy to the source. The user always decides, it
// never happens automatically. The suggestion only renders for users
// authorized to link.
export const DuplicateJobConfirmationDialog = ({
  isError,
  isLoading,
  onConfirm,
  onOpenChange,
  open,
}: {
  isError?: boolean;
  isLoading?: boolean;
  onConfirm: (linkToSource: boolean) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  /* React Hooks */
  const canLink = useCanLinkJobs();
  const [linkToSource, setLinkToSource] = useState(false);

  // Reset when the dialog closes, however it was closed — the success path
  // closes programmatically via the controlled `open` prop, which Radix does
  // not report through onOpenChange. Without this, a checked box silently
  // carries into the next duplicate.
  useEffect(() => {
    if (!open) {
      setLinkToSource(false);
    }
  }, [open]);

  /* Render */
  const render = () => {
    return (
      <ConfirmationDialog
        confirmButtonText="Duplicate"
        description="Are you sure you want to duplicate this job? This will create a copy of the job in the draft state."
        errorMessage="Failed to duplicate the job. Please try again."
        isError={isError}
        isLoading={isLoading}
        onConfirm={() => {
          onConfirm(linkToSource);
        }}
        onOpenChange={onOpenChange}
        open={open}
        title="Duplicate Job"
      >
        {canLink && (
          <Row className="items-center gap-2">
            <Checkbox
              checked={linkToSource}
              id="link-to-source"
              onCheckedChange={(checked) => {
                setLinkToSource(checked === true);
              }}
            />
            <Label
              className="cursor-pointer text-base text-slate-700"
              htmlFor="link-to-source"
            >
              Link the copy to this job (versions of the same job)
            </Label>
          </Row>
        )}
      </ConfirmationDialog>
    );
  };

  return render();
};
